/**
 * RGM Coin Rush — Phase 3 Public Artist Experience
 * ================================================
 *
 * Public leaderboard + personal standing page at `/coin-rush`.
 *
 * Reads ONLY these Phase 1 APIs — never writes to anything:
 *   GET /api/coin-rush/active        (public, no auth)
 *   GET /api/coin-rush/leaderboard   (public, no auth)
 *   GET /api/coin-rush/my-score      (auth-gated)
 *
 * Financial boundary:
 *   • Points are rendered as "pts" / "Coin Rush Points" only.
 *   • NEVER formatted as currency or any clearance / balance term.
 *   • No gift/coin/earnings/transfer writes — this page is read-only.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import SharedNavigation from "@/components/SharedNavigation";
import { optimizeImage } from "@/lib/mediaUrls";

// Phase 1 scoring — exposed to the public page so the "next bonus"
// callout can derive progress from unique_artists_supported alone.
const MILESTONES = [
  { threshold: 5, bonus: 25 },
  { threshold: 10, bonus: 75 },
  { threshold: 25, bonus: 250 },
];

// Pretty UTC → local date for the countdown ribbon and headers.
const fmtEndLocal = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString(undefined, {
      weekday: "short", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch {
    return "";
  }
};

// Humanize the active month into a header subtitle such as
// "October 2026" from month_key = "2026-10" (fall back to title).
const monthHeader = (month) => {
  if (!month) return "";
  const key = month.month_key || "";
  const m = key.match(/^(\d{4})-(\d{2})$/);
  if (!m) return month.title || "";
  const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const idx = parseInt(m[2], 10) - 1;
  return `${months[idx] || ""} ${m[1]}`.trim();
};

// Countdown-left formatter — "3d 4h 12m" style. Returns "" when ended.
const formatCountdown = (ms) => {
  if (!ms || ms <= 0) return "";
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${mins}m`;
  if (h > 0) return `${h}h ${mins}m`;
  return `${mins}m`;
};

const Stat = ({ label, value, testId, highlight }) => (
  <div
    className={`rounded-xl p-3 border ${
      highlight
        ? "bg-amber-500/10 border-amber-400/40"
        : "bg-white/[0.03] border-white/10"
    }`}
    data-testid={testId}
  >
    <p className="text-[10px] uppercase tracking-widest text-pink-200/60">{label}</p>
    <p className={`mt-1 font-black ${highlight ? "text-amber-200 text-2xl" : "text-white text-xl"}`}>
      {value ?? "—"}
    </p>
  </div>
);

const PodiumCard = ({ row, place }) => {
  const styles = {
    1: {
      ring: "ring-amber-400/70",
      bg: "from-amber-500/25 via-amber-400/10 to-transparent",
      medal: "🥇",
      pts: "text-amber-200",
    },
    2: {
      ring: "ring-zinc-300/60",
      bg: "from-zinc-300/20 via-zinc-200/5 to-transparent",
      medal: "🥈",
      pts: "text-zinc-100",
    },
    3: {
      ring: "ring-orange-400/60",
      bg: "from-orange-500/20 via-orange-400/5 to-transparent",
      medal: "🥉",
      pts: "text-orange-200",
    },
  }[place];
  if (!row) {
    return (
      <div
        className={`relative rounded-2xl p-4 border border-white/5 bg-white/[0.02] ${
          place === 1 ? "sm:order-2 sm:-mt-2" : place === 2 ? "sm:order-1" : "sm:order-3"
        }`}
        data-testid={`coinrush-podium-empty-${place}`}
      >
        <p className="text-xs text-gray-500">Spot open · support an artist to claim</p>
      </div>
    );
  }
  return (
    <Card
      className={`relative rounded-2xl p-4 border border-white/10 bg-gradient-to-b ${styles.bg} ring-1 ${styles.ring} ${
        place === 1 ? "sm:order-2 sm:-mt-4 sm:scale-[1.02]" : place === 2 ? "sm:order-1" : "sm:order-3"
      }`}
      data-testid={`coinrush-podium-${place}`}
    >
      <div className="flex items-start gap-3">
        <div className="text-3xl leading-none" aria-hidden="true">{styles.medal}</div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-widest text-white/60">Rank #{row.rank}</p>
          <div className="flex items-center gap-2 mt-1">
            {row.profile_photo ? (
              <img
                src={optimizeImage(row.profile_photo, 'avatar')}
                alt=""
                className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex-shrink-0" />
            )}
            <p className="text-white font-black truncate text-base">{row.artist_name}</p>
          </div>
          <p className={`mt-2 font-black text-2xl tabular-nums ${styles.pts}`}>
            {row.points} <span className="text-xs text-white/70 font-bold">pts</span>
          </p>
          <p className="text-[11px] text-white/70 mt-1">
            {row.unique_artists_supported} artists · {row.qualifying_gift_count} gifts
          </p>
        </div>
      </div>
    </Card>
  );
};

const CoinRushPage = () => {
  const [me, setMe] = useState(null);
  const [authResolved, setAuthResolved] = useState(false);
  const [active, setActive] = useState(null);        // GET /coin-rush/active
  const [leaderboard, setLeaderboard] = useState(null); // GET /coin-rush/leaderboard
  const [lbTotal, setLbTotal] = useState(0);
  const [myScore, setMyScore] = useState(null);      // GET /coin-rush/my-score
  const [myScoreError, setMyScoreError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => Date.now());

  // Resolve the signed-in user non-blockingly. If the user is not
  // signed in, /auth/me will 401 and we'll just render the public bits.
  useEffect(() => {
    let cancelled = false;
    api.get("/auth/me")
      .then((r) => { if (!cancelled) setMe(r.data || null); })
      .catch(() => { if (!cancelled) setMe(null); })
      .finally(() => { if (!cancelled) setAuthResolved(true); });
    return () => { cancelled = true; };
  }, []);

  const loadPublic = useCallback(async () => {
    try {
      const [a, lb] = await Promise.all([
        api.get("/coin-rush/active"),
        api.get("/coin-rush/leaderboard", { params: { limit: 10 } }),
      ]);
      setActive(a.data || null);
      setLeaderboard(Array.isArray(lb.data?.leaderboard) ? lb.data.leaderboard : []);
      setLbTotal(Number(lb.data?.total_participants || 0));
    } catch {
      setActive(null);
      setLeaderboard([]);
      setLbTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMyScore = useCallback(async () => {
    if (!me) {
      setMyScore(null);
      return;
    }
    try {
      const r = await api.get("/coin-rush/my-score");
      setMyScore(r.data || null);
      setMyScoreError(null);
    } catch (e) {
      setMyScore(null);
      setMyScoreError(e?.response?.data?.detail || null);
    }
  }, [me]);

  useEffect(() => { loadPublic(); }, [loadPublic]);
  useEffect(() => { if (authResolved) loadMyScore(); }, [authResolved, loadMyScore]);

  // Countdown tick — only runs while a month is active with a future
  // end_at. Otherwise idle to keep this page cheap.
  useEffect(() => {
    const endIso = active?.month?.end_at;
    if (!active?.active || !endIso) return;
    const t = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => clearInterval(t);
  }, [active]);

  const month = active?.month || null;
  const monthLabel = useMemo(() => monthHeader(month), [month]);

  const endMs = useMemo(() => {
    const iso = month?.end_at;
    if (!iso) return null;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d.getTime();
  }, [month]);
  const timeLeftMs = endMs ? endMs - now : null;
  const countdown = formatCountdown(timeLeftMs);

  const scoreBlock = myScore?.score || null;
  const uniqueArtists = scoreBlock?.unique_artists_supported ?? 0;

  // Next-milestone progress is derivable from unique_artists_supported
  // alone — no fabrication.
  const nextMilestone = useMemo(() => {
    if (!scoreBlock) return null;
    for (const m of MILESTONES) {
      if (uniqueArtists < m.threshold) {
        return {
          threshold: m.threshold,
          bonus: m.bonus,
          remaining: m.threshold - uniqueArtists,
          progressPct: Math.min(100, Math.round((uniqueArtists / m.threshold) * 100)),
        };
      }
    }
    return null;
  }, [scoreBlock, uniqueArtists]);

  const inTopTen = useMemo(() => {
    if (!scoreBlock || !leaderboard) return false;
    return (leaderboard || []).some((r) => r.sender_id === scoreBlock.sender_id);
  }, [scoreBlock, leaderboard]);

  // Build the podium (ranks 1–3) and the remaining rows (4–10).
  const podium = useMemo(() => {
    const p = { 1: null, 2: null, 3: null };
    for (const r of leaderboard || []) {
      if (r.rank === 1) p[1] = r;
      else if (r.rank === 2) p[2] = r;
      else if (r.rank === 3) p[3] = r;
    }
    return p;
  }, [leaderboard]);
  const restRows = useMemo(() => (leaderboard || []).filter((r) => r.rank > 3 && r.rank <= 10), [leaderboard]);

  const headerTitle = monthLabel ? `COIN RUSH — ${monthLabel.toUpperCase()}` : "COIN RUSH";

  return (
    <div className="min-h-screen bg-[#08080c] text-white" data-testid="coinrush-public-page">
      <SharedNavigation currentUser={me} activePage="coin-rush" />

      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
        {/* Hero */}
        <div
          className="relative overflow-hidden rounded-3xl border border-amber-500/20 bg-gradient-to-br from-rose-600/20 via-fuchsia-600/15 to-amber-500/15 p-6 sm:p-8 mb-6"
          data-testid="coinrush-hero"
        >
          <div className="absolute -top-20 -right-20 w-72 h-72 bg-amber-500/20 blur-3xl rounded-full pointer-events-none" />
          <div className="relative">
            <p className="text-[10px] uppercase tracking-[0.25em] text-amber-200/80 font-black">
              Live Monthly Competition
            </p>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black mt-2 tracking-tight leading-tight">
              🔥 {headerTitle}
            </h1>
            <p className="text-pink-100/90 text-base sm:text-lg mt-3 max-w-2xl">
              Support Artists. Earn Points. Climb the Rush.
            </p>
            <p
              className="text-[11px] sm:text-xs text-amber-100/70 mt-4 max-w-2xl"
              data-testid="coinrush-disclaimer"
            >
              Coin Rush Points are promotional competition points only. They are
              <strong className="text-amber-100"> not coins, cash, or Cashable Earnings</strong>.
            </p>

            <div className="mt-5 flex items-center gap-3 flex-wrap">
              {active?.active ? (
                <>
                  <span
                    className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/50 text-emerald-100 text-xs font-black"
                    data-testid="coinrush-active-badge"
                  >
                    <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                    LIVE NOW
                  </span>
                  {countdown && (
                    <span
                      className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-black/40 border border-white/15 text-amber-200 text-xs font-black tabular-nums"
                      data-testid="coinrush-countdown"
                      title={`Ends ${fmtEndLocal(month?.end_at)}`}
                    >
                      ⏳ {countdown} left
                    </span>
                  )}
                  <span
                    className="text-[11px] text-white/70"
                    data-testid="coinrush-endsat"
                  >
                    Ends {fmtEndLocal(month?.end_at)}
                  </span>
                </>
              ) : month?.enabled && month?.start_at ? (
                <span
                  className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/15 text-amber-100 text-xs font-black"
                  data-testid="coinrush-scheduled-badge"
                >
                  ⏱ Starts {fmtEndLocal(month.start_at)}
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/15 text-white/80 text-xs font-black"
                  data-testid="coinrush-inactive-badge"
                >
                  💤 Not currently running
                </span>
              )}
            </div>
          </div>
        </div>

        {/* My Coin Rush — only when signed in */}
        {authResolved && me && (
          <section className="mb-8" data-testid="coinrush-my-section">
            <h2 className="text-xl sm:text-2xl font-black text-white mb-3">My Coin Rush</h2>

            {!active?.active ? (
              <Card
                className="p-4 bg-white/[0.03] border-white/10 text-sm text-white/70"
                data-testid="coinrush-my-inactive"
              >
                Your personal standing will appear here as soon as the next Coin Rush goes live.
              </Card>
            ) : myScoreError ? (
              <Card
                className="p-4 bg-rose-500/10 border-rose-500/30 text-sm text-rose-200"
                data-testid="coinrush-my-error"
              >
                We couldn't load your Coin Rush standing. Try again shortly.
              </Card>
            ) : myScore?.disqualified ? (
              <Card
                className="p-4 bg-rose-500/10 border-rose-500/30 text-sm text-rose-200"
                data-testid="coinrush-my-disqualified"
              >
                You've been excluded from this month's Coin Rush. Reach out to the RGM team
                if you believe this is a mistake.
              </Card>
            ) : !scoreBlock ? (
              <Card className="p-4 bg-white/[0.03] border-white/10 text-sm text-white/70">
                Loading your standing…
              </Card>
            ) : scoreBlock.qualifying_gift_count === 0 ? (
              <Card
                className="p-5 bg-gradient-to-br from-purple-700/20 to-fuchsia-600/10 border-purple-400/30"
                data-testid="coinrush-my-zero"
              >
                <p className="text-white font-black text-lg">
                  You haven't earned any Coin Rush points yet 🚀
                </p>
                <p className="text-white/70 text-sm mt-1">
                  Send a paid-backed gift to any artist to score your first point and the
                  <strong className="text-amber-200"> +2 diversity bonus</strong> for each new
                  artist you support.
                </p>
                <div className="mt-4 flex gap-2 flex-wrap">
                  <Link to="/explore">
                    <Button className="bg-amber-500 hover:bg-amber-400 text-black font-black" data-testid="coinrush-my-explore-cta">
                      🎧 Discover artists
                    </Button>
                  </Link>
                  <Link to="/gifts">
                    <Button variant="outline" className="text-white border-white/20" data-testid="coinrush-my-gifts-cta">
                      🎁 Browse gifts
                    </Button>
                  </Link>
                </div>
              </Card>
            ) : (
              <Card className="p-5 bg-gradient-to-br from-purple-700/15 to-amber-500/5 border-amber-400/20" data-testid="coinrush-my-card">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                  <Stat
                    label="My Rank"
                    value={scoreBlock.rank ? `#${scoreBlock.rank}` : "—"}
                    testId="coinrush-my-rank"
                    highlight
                  />
                  <Stat
                    label="My Points"
                    value={scoreBlock.points ?? 0}
                    testId="coinrush-my-points"
                    highlight
                  />
                  <Stat
                    label="Qualifying Gifts"
                    value={scoreBlock.qualifying_gift_count ?? 0}
                    testId="coinrush-my-gifts"
                  />
                  <Stat
                    label="Unique Artists"
                    value={scoreBlock.unique_artists_supported ?? 0}
                    testId="coinrush-my-unique"
                  />
                  <Stat
                    label="Base Points"
                    value={scoreBlock.base_gift_points ?? 0}
                    testId="coinrush-my-base"
                  />
                  <Stat
                    label="Diversity Points"
                    value={scoreBlock.diversity_points ?? 0}
                    testId="coinrush-my-diversity"
                  />
                  <Stat
                    label="Milestone Points"
                    value={scoreBlock.milestone_points ?? 0}
                    testId="coinrush-my-milestone"
                  />
                  <Stat
                    label="Participants"
                    value={myScore?.score?.total_participants ?? lbTotal}
                    testId="coinrush-my-total-participants"
                  />
                </div>

                {nextMilestone ? (
                  <div
                    className="mt-4 rounded-xl bg-black/30 border border-amber-400/30 p-4"
                    data-testid="coinrush-next-bonus"
                  >
                    <p className="text-[10px] uppercase tracking-[0.25em] text-amber-200/80 font-black">
                      🔥 Next Bonus
                    </p>
                    <p className="text-white font-black mt-1" data-testid="coinrush-next-bonus-copy">
                      Support <span className="text-amber-200">{nextMilestone.remaining} more</span>{" "}
                      unique artist{nextMilestone.remaining === 1 ? "" : "s"} to unlock{" "}
                      <span className="text-amber-200">+{nextMilestone.bonus} points</span>.
                    </p>
                    <div className="mt-3 h-2 w-full rounded-full bg-white/5 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-amber-500 via-amber-400 to-rose-500"
                        style={{ width: `${nextMilestone.progressPct}%` }}
                        data-testid="coinrush-next-bonus-progress"
                      />
                    </div>
                    <p className="text-[11px] text-white/60 mt-2">
                      {uniqueArtists} / {nextMilestone.threshold} unique artists supported
                    </p>
                  </div>
                ) : (
                  <div
                    className="mt-4 rounded-xl bg-emerald-500/10 border border-emerald-400/30 p-4"
                    data-testid="coinrush-milestones-complete"
                  >
                    <p className="text-emerald-200 font-black">
                      🏆 Legend tier — you've hit every V1 milestone this month.
                    </p>
                  </div>
                )}
              </Card>
            )}
          </section>
        )}

        {/* Sign-in nudge for unauthenticated visitors */}
        {authResolved && !me && (
          <Card
            className="p-5 mb-8 bg-white/[0.03] border-white/10"
            data-testid="coinrush-signin-nudge"
          >
            <p className="text-white font-black text-base">Sign in to join the Rush</p>
            <p className="text-white/70 text-sm mt-1">
              Create or sign into your RGM account to start earning Coin Rush points.
            </p>
            <div className="mt-3">
              <Link to="/login">
                <Button className="bg-purple-600 hover:bg-purple-500 text-white font-black" data-testid="coinrush-signin-cta">
                  Artist Portal →
                </Button>
              </Link>
            </div>
          </Card>
        )}

        {/* Leaderboard — Top 10 */}
        <section className="mb-10" data-testid="coinrush-leaderboard-section">
          <div className="flex items-end justify-between gap-2 mb-4 flex-wrap">
            <h2 className="text-xl sm:text-2xl font-black text-white">Top 10 This Month</h2>
            <span className="text-[11px] text-white/60" data-testid="coinrush-public-total">
              {lbTotal} participant{lbTotal === 1 ? "" : "s"}
            </span>
          </div>

          {loading ? (
            <p className="text-sm text-white/50">Loading…</p>
          ) : !active?.active ? (
            <Card
              className="p-5 bg-white/[0.03] border-white/10 text-sm text-white/70"
              data-testid="coinrush-lb-inactive"
            >
              Coin Rush isn't live right now. The next competition's leaderboard will appear here as soon as it opens.
            </Card>
          ) : (leaderboard || []).length === 0 ? (
            <Card
              className="p-5 bg-white/[0.03] border-white/10 text-sm text-white/70"
              data-testid="coinrush-lb-empty"
            >
              Nobody's on the board yet — be the first to send a paid-backed gift and claim #1.
            </Card>
          ) : (
            <>
              {/* Podium */}
              <div
                className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4 items-end"
                data-testid="coinrush-podium-grid"
              >
                <PodiumCard row={podium[2]} place={2} />
                <PodiumCard row={podium[1]} place={1} />
                <PodiumCard row={podium[3]} place={3} />
              </div>

              {/* Ranks 4–10 */}
              {restRows.length > 0 && (
                <Card
                  className="p-3 bg-white/[0.03] border-white/10"
                  data-testid="coinrush-lb-rest"
                >
                  <ul className="divide-y divide-white/5">
                    {restRows.map((r) => {
                      const isMe = scoreBlock && r.sender_id === scoreBlock.sender_id;
                      return (
                        <li
                          key={r.sender_id}
                          className={`flex items-center gap-3 py-2 px-1 ${isMe ? "bg-amber-500/5 rounded-md" : ""}`}
                          data-testid={`coinrush-lb-row-${r.rank}`}
                        >
                          <span className="text-white/70 text-sm font-black w-8 text-center">#{r.rank}</span>
                          {r.profile_photo ? (
                            <img
                              src={optimizeImage(r.profile_photo, 'avatar')}
                              alt=""
                              className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                              onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex-shrink-0" />
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-white truncate text-sm font-bold">
                              {r.artist_name}
                              {isMe && (
                                <span className="ml-2 text-[10px] text-amber-200 font-black">YOU</span>
                              )}
                            </p>
                            <p className="text-[11px] text-white/50 truncate">
                              {r.unique_artists_supported} artists · {r.qualifying_gift_count} gifts
                            </p>
                          </div>
                          <p className="text-amber-200 font-black tabular-nums">
                            {r.points} <span className="text-[10px] text-white/60">pts</span>
                          </p>
                        </li>
                      );
                    })}
                  </ul>
                </Card>
              )}

              {/* Your rank outside Top 10 */}
              {scoreBlock && !inTopTen && scoreBlock.rank && (
                <Card
                  className="mt-3 p-4 bg-purple-500/10 border-purple-400/30 flex items-center justify-between gap-3"
                  data-testid="coinrush-your-rank-outside"
                >
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-purple-200/80 font-black">
                      Your Rank
                    </p>
                    <p className="text-white font-black text-2xl mt-1">
                      #{scoreBlock.rank}{" "}
                      <span className="text-white/60 text-sm font-bold">
                        of {myScore?.score?.total_participants ?? lbTotal}
                      </span>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-amber-200 font-black text-2xl tabular-nums">
                      {scoreBlock.points}
                    </p>
                    <p className="text-[10px] text-white/60">points</p>
                  </div>
                </Card>
              )}
            </>
          )}
        </section>

        {/* How to Earn */}
        <section className="mb-10" data-testid="coinrush-howto-section">
          <h2 className="text-xl sm:text-2xl font-black text-white mb-3">How to Earn</h2>
          <Card className="p-5 bg-white/[0.03] border-white/10">
            <ul className="space-y-2 text-sm text-white/90">
              <li className="flex items-start gap-3" data-testid="coinrush-rule-gift">
                <span className="text-xl leading-none">🎁</span>
                <span>
                  Send a <strong>paid-backed gift</strong> to any artist · <strong className="text-amber-200">+1 point</strong>
                </span>
              </li>
              <li className="flex items-start gap-3" data-testid="coinrush-rule-diversity">
                <span className="text-xl leading-none">🤝</span>
                <span>
                  The <strong>first</strong> qualifying gift to a different artist this month · <strong className="text-amber-200">+2 bonus</strong>
                </span>
              </li>
              <li className="flex items-start gap-3" data-testid="coinrush-rule-m5">
                <span className="text-xl leading-none">🌎</span>
                <span>
                  Support <strong>5 unique artists</strong> · <strong className="text-amber-200">+25 points</strong>
                </span>
              </li>
              <li className="flex items-start gap-3" data-testid="coinrush-rule-m10">
                <span className="text-xl leading-none">🌎</span>
                <span>
                  Support <strong>10 unique artists</strong> · <strong className="text-amber-200">+75 points</strong>
                </span>
              </li>
              <li className="flex items-start gap-3" data-testid="coinrush-rule-m25">
                <span className="text-xl leading-none">🌎</span>
                <span>
                  Support <strong>25 unique artists</strong> · <strong className="text-amber-200">+250 points</strong>
                </span>
              </li>
            </ul>
            <p className="text-[11px] text-white/60 mt-4" data-testid="coinrush-rule-footnote">
              Milestones are cumulative — hit all three and you'll bank 25 + 75 + 250 bonus points on top of every base and diversity point.
              Reward-only and test-only gifts do not qualify. Coin Rush Points are promotional only — they are never coins, cash, or Cashable Earnings.
            </p>
          </Card>
        </section>
      </div>
    </div>
  );
};

export default CoinRushPage;
